/**
 * Admin service - wraps the admin callable Cloud Functions
 *
 * All calls require the signed-in user to have the `admin` custom claim;
 * the functions reject everything else with permission-denied.
 */

import { functions, httpsCallable } from '@/lib/firebase'
import { FeatureFlag } from '@/constants/featureFlags'
import type { AdminUserListItem, SubscriptionInput } from '@/types/admin'

const listUsersFn = httpsCallable<void, { users: AdminUserListItem[] }>(
  functions,
  'adminListUsers'
)

const getUserFn = httpsCallable<{ uid: string }, { user: AdminUserListItem }>(
  functions,
  'adminGetUser'
)

const setFeatureFlagsFn = httpsCallable<
  { uid: string; flags: Partial<Record<FeatureFlag, boolean>> },
  { success: boolean }
>(functions, 'adminSetFeatureFlags')

const setSubscriptionFn = httpsCallable<
  { uid: string; subscription: SubscriptionInput },
  { success: boolean }
>(functions, 'adminSetSubscription')

export const adminService = {
  async listUsers(): Promise<AdminUserListItem[]> {
    const result = await listUsersFn()
    return result.data.users
  },

  async getUser(uid: string): Promise<AdminUserListItem> {
    const result = await getUserFn({ uid })
    return result.data.user
  },

  async setFeatureFlags(
    uid: string,
    flags: Partial<Record<FeatureFlag, boolean>>
  ): Promise<void> {
    await setFeatureFlagsFn({ uid, flags })
  },

  async setSubscription(
    uid: string,
    subscription: SubscriptionInput
  ): Promise<void> {
    await setSubscriptionFn({ uid, subscription })
  },
}
